import React, { useState, useMemo } from 'react';
import { Table, Button, Modal, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { formatPrecio } from '../../utils/formatters';
import { obtenerNombreProducto } from '../../utils/productUtils';
import { ROUTES } from '../../constants';
import { eliminarProducto } from '../../services/productoService';
import { eliminarImagen } from '../../services/imagenService';

/**
 * Componente de tabla para mostrar productos
 * @param {Object} props
 * @param {Array} props.productos - Lista de productos a mostrar
 * @param {Function} props.onEliminar - Callback cuando se elimina un producto
 */
function TablaProductos({ productos, onEliminar }) {
  const navigate = useNavigate();
  const [showConfirmEliminar, setShowConfirmEliminar] = useState(false);
  const [productoAEliminar, setProductoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' o 'desc'

  const handleEditar = (id) => {
    navigate(`${ROUTES.ADMIN_PRODUCTOS}/editar/${id}`);
  };

  const handleEliminar = (producto) => {
    setProductoAEliminar(producto);
    setShowConfirmEliminar(true);
    setErrorEliminar(null);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Si ya está ordenando por esta columna, cambiar la dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es una nueva columna, ordenar ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Ordenar productos según la columna y dirección seleccionadas
  const productosOrdenados = useMemo(() => {
    if (!sortColumn) return productos;

    return [...productos].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Manejar valores null/undefined para stock
      if (sortColumn === 'stock') {
        aValue = aValue != null ? Number(aValue) : -Infinity;
        bValue = bValue != null ? Number(bValue) : -Infinity;
      } else {
        // Para otros campos, convertir a string para comparar
        aValue = aValue != null ? String(aValue).toLowerCase() : '';
        bValue = bValue != null ? String(bValue).toLowerCase() : '';
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [productos, sortColumn, sortDirection]);

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  // Calcular el stock total de los productos mostrados
  const stockTotal = useMemo(() => {
    return productosOrdenados.reduce((total, producto) => {
      const stock = producto.stock != null ? Number(producto.stock) : 0;
      return total + stock;
    }, 0);
  }, [productosOrdenados]);

  const confirmarEliminar = async () => {
    if (!productoAEliminar) return;

    setEliminando(true);
    setErrorEliminar(null);

    try {
      // Solo borrar archivo en disco si fue subida vía API (evita borrar assets estáticos del repo)
      if (
        productoAEliminar.imagenGestionadaPorApi === true &&
        productoAEliminar.imagenURL &&
        !String(productoAEliminar.imagenURL).includes('http') &&
        !String(productoAEliminar.imagenURL).includes('firebasestorage')
      ) {
        try {
          await eliminarImagen(productoAEliminar.imagenURL);
        } catch (imgError) {
          console.warn('Error al eliminar imagen del producto:', imgError);
        }
      }

      // Eliminar el producto
      await eliminarProducto(productoAEliminar.id);
      
      // Cerrar modal y refrescar lista
      setShowConfirmEliminar(false);
      setProductoAEliminar(null);
      
      if (onEliminar) {
        onEliminar();
      }
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      setErrorEliminar(error.message || 'Error al eliminar el producto');
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="admin-pedidos-table">
      <Table hover responsive className="admin-table-modern">
        <thead>
          <tr>
            <th className="text-start">Modelo</th>
            <th className="text-start">Color</th>
            <th className="text-start">Material</th>
            <th className="text-start">Categoría</th>
            <th className="text-start">Precio (mayorista)</th>
            <th className="text-start">Precio (minorista)</th>
            <th 
              className="text-start" 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('stock')}
            >
              Stock {getSortIcon('stock')}
            </th>
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {productosOrdenados.map(producto => (
            <tr key={producto.id}>
              <td className="text-start">{producto.modelo || '-'}</td>
              <td className="text-start">{producto.color || '-'}</td>
              <td className="text-start">{producto.material || '-'}</td>
              <td className="text-start">{producto.categoria || '-'}</td>
              <td className="text-start">{formatPrecio(producto.precio)}</td>
              <td className="text-start">{formatPrecio(producto.preciomenor)}</td>
              <td className="text-start">{producto.stock != null ? producto.stock : '-'}</td>
              <td className="text-center">
                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleEditar(producto.id)}
                    aria-label="Editar producto"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleEliminar(producto)}
                    aria-label="Eliminar producto"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
            <td colSpan="6" className="text-end">
              <strong>Stock Total:</strong>
            </td>
            <td className="text-start">
              <strong>{stockTotal.toLocaleString()}</strong>
            </td>
            <td></td>
          </tr>
        </tfoot>
      </Table>

      {/* Modal de confirmación para eliminar producto */}
      <Modal 
        show={showConfirmEliminar} 
        onHide={() => {
          if (!eliminando) {
            setShowConfirmEliminar(false);
            setProductoAEliminar(null);
            setErrorEliminar(null);
          }
        }} 
        centered
        className="admin-modal-confirm"
      >
        <Modal.Header closeButton className="admin-modal-header">
          <Modal.Title className="admin-modal-title">Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body className="admin-modal-body">
          {errorEliminar ? (
            <Alert variant="danger" className="mb-0">{errorEliminar}</Alert>
          ) : (
            <>
              ¿Está seguro que desea eliminar el producto <strong>{productoAEliminar ? obtenerNombreProducto(productoAEliminar) : ''}</strong>?
              <br />
              <small className="text-muted">Esta acción no se puede deshacer.</small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => {
              if (!eliminando) {
                setShowConfirmEliminar(false);
                setProductoAEliminar(null);
                setErrorEliminar(null);
              }
            }} 
            className="admin-modal-btn"
            disabled={eliminando}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmarEliminar} 
            className="admin-modal-btn"
            disabled={eliminando}
          >
            {eliminando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TablaProductos;

